import { useState, useMemo } from 'react';

/**
 * Custom hook for managing expandable text content (show more/less)
 * @param {string} content - The full content to be shown/hidden
 * @param {number} previewLength - Number of words to show in collapsed state (default: 15)
 * @returns {object} Expandable state and content
 */
function useExpandable(content, previewLength = 15) {
  const [isExpanded, setIsExpanded] = useState(false);

  const { preview, needsExpansion } = useMemo(() => {
    if (!content) {
      return { preview: '', needsExpansion: false };
    }

    const words = content.trim().split(/\s+/);
    
    if (words.length <= previewLength) {
      return { preview: content, needsExpansion: false };
    }

    const previewText = words.slice(0, previewLength).join(' ');
    return { preview: previewText, needsExpansion: true };
  }, [content, previewLength]);

  const displayContent = isExpanded || !needsExpansion ? content : preview;

  const toggle = () => setIsExpanded(prev => !prev);

  return {
    displayContent,
    isExpanded,
    needsExpansion,
    toggle,
    setIsExpanded,
  };
}

export default useExpandable;
